import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

export interface UnlimitedStatus {
  isUnlimited: boolean;
  startsAt: Date | null;
  expiresAt: Date | null;
  daysRemaining: number | null;
}

export function useUnlimitedStatus() {
  const { isAuthenticated, userId } = useAuth();

  return useQuery({
    queryKey: ["unlimited-status", userId],
    queryFn: async (): Promise<UnlimitedStatus> => {
      const { data, error } = await supabase
        .from("user_memberships")
        .select("is_unlimited, unlimited_expires_at, unlimited_starts_at")
        .eq("user_id", userId!)
        .single();

      if (error || !data) {
        return { isUnlimited: false, startsAt: null, expiresAt: null, daysRemaining: null };
      }

      const startsAt = data.unlimited_starts_at
        ? new Date(data.unlimited_starts_at)
        : null;
      const expiresAt = data.unlimited_expires_at
        ? new Date(data.unlimited_expires_at)
        : null;

      const now = new Date();

      // Check if unlimited is active: enabled, has started, and not expired
      const hasStarted = startsAt && startsAt <= now;
      const notExpired = expiresAt && expiresAt > now;
      const isActive = data.is_unlimited && hasStarted && notExpired;

      if (!isActive) {
        return { isUnlimited: false, startsAt: null, expiresAt: null, daysRemaining: null };
      }

      // Calculate days remaining
      const diffTime = expiresAt!.getTime() - now.getTime();
      const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return {
        isUnlimited: true,
        startsAt,
        expiresAt,
        daysRemaining,
      };
    },
    enabled: isAuthenticated && !!userId,
  });
}
